import '@/styles/fonts.css';
import { enableMapSet } from "immer";
enableMapSet();
import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useParams,
} from "react-router-dom";

// Redirect component for legacy novella URLs
function LegacyNovellaRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/book/${id}`} replace />;
}
import { ClerkProvider } from '@clerk/clerk-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { AppLayout } from '@/layouts/AppLayout';
import '@/index.css'
import { HomePage } from '@/pages/HomePage'
import { Skeleton } from './components/ui/skeleton';
const WorkspacePage = lazy(() => import('@/pages/WorkspacePage'));
const MyNovellasPage = lazy(() => import('@/pages/MyNovellasPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const BrowsePage = lazy(() => import('@/pages/BrowsePage'));
const SharedNovellaPage = lazy(() => import('@/pages/SharedNovellaPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

const router = createBrowserRouter([
  {
    path: "/",
    element: <HomePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/app",
    element: <AppLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="/app/workspace" replace />,
      },
      {
        path: "workspace",
        element: (
          <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full bg-brand-accent/20" /></div>}>
            <WorkspacePage />
          </Suspense>
        ),
      },
      {
        path: "library",
        element: (
          <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full bg-brand-accent/20" /></div>}>
            <MyNovellasPage />
          </Suspense>
        ),
      },
      // Settings route disabled - webhook configuration is admin-only
      // {
      //   path: "settings",
      //   element: (
      //     <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full bg-brand-accent/20" /></div>}>
      //       <SettingsPage />
      //     </Suspense>
      //   ),
      // },
    ],
  },
  {
    path: "/my-novellas",
    element: <Navigate to="/app/library" replace />,
  },
  {
    path: "/pricing",
    element: (
      <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full" /></div>}>
        <PricingPage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/browse",
    element: (
      <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full" /></div>}>
        <BrowsePage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/privacy",
    element: (
      <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full" /></div>}>
        <PrivacyPage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/terms",
    element: (
      <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full" /></div>}>
        <TermsPage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Primary SEO URL: /u/username/title-slug-shortId (using /u/ to avoid Cloudflare @ encoding)
    path: "/u/:username/:slug",
    element: (
      <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full" /></div>}>
        <SharedNovellaPage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Fallback without username: /book/:id (full UUID)
    path: "/book/:id",
    element: (
      <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full" /></div>}>
        <SharedNovellaPage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Legacy /@username/slug route - redirect to /u/username/slug (Cloudflare encodes @)
    path: "/@:username/:slug",
    element: <Navigate to={`/u/:username/:slug`} replace />,
  },
  {
    // Legacy /novella/:id route - redirect to /book/:id (Cloudflare blocks /novella)
    path: "/novella/:id",
    element: <LegacyNovellaRedirect />,
  },
  {
    // Legacy route - redirect to new simpler URL
    path: "/:username/novellas/:id",
    element: <LegacyNovellaRedirect />,
  },
  {
    path: "*",
    element: (
      <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Skeleton className="h-20 w-20 rounded-full" /></div>}>
        <NotFoundPage />
      </Suspense>
    ),
  }
]);
// Clerk publishable key - get this from https://dashboard.clerk.com
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  console.warn('Missing Clerk Publishable Key. Set VITE_CLERK_PUBLISHABLE_KEY in your .env file.');
}

// Do not touch this code
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignInUrl="/app/workspace" afterSignOutUrl="/">
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
    </ClerkProvider>
  </StrictMode>,
)